import './event-card.scss';
import JoinButton from '../join-button/join-button';
import PdfPreview from '../pdf-preview/pdf-preview';
import EventSponsors from '../event-sponsors/event-sponsors';
import {
  formatEventDate,
  hasEventEnded,
  isPdf,
} from '../../helpers/eventHelpers';
import { isMobileSafari } from '../../utils/browserUtils';
import type { EventSchedule } from '../event-schedule/event-schedule';

type EventCardProps = {
  event: EventSchedule;
  images: string[];
  currentImage?: string;
  currentIndex: number;
  showAll: boolean;
  hash: string;
  enableImageModal: boolean;
  compactHomePreview: boolean;
  onImageChange: (direction: -1 | 1) => void;
  onOpenImageViewer: () => void;
};

const EventCard = ({
  event,
  images,
  currentImage,
  currentIndex,
  showAll,
  hash,
  enableImageModal,
  compactHomePreview,
  onImageChange,
  onOpenImageViewer,
}: EventCardProps) => {
  const currentFileIsPdf = isPdf(event.images?.[currentIndex] ?? event.image);
  const useMobileSafariPdfButton = enableImageModal && currentFileIsPdf && isMobileSafari();
  const isHighlighted = showAll && hash === `#event-${event.id}`;
  const cardClassName = `event-card${compactHomePreview ? ' event-card--home-preview' : ''}${isHighlighted ? ' event-card--highlighted' : ''}`;
  const navigateToSchedule = () => {
    window.location.href = `/schedule#event-${event.id}`;
  };

  return (
    <article
      id={`event-${event.id}`}
      className={cardClassName}
      role={compactHomePreview ? 'link' : undefined}
      tabIndex={compactHomePreview ? 0 : undefined}
      onClick={compactHomePreview ? navigateToSchedule : undefined}
      onKeyDown={compactHomePreview ? (keyboardEvent) => {
        if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
          keyboardEvent.preventDefault();
          navigateToSchedule();
        }
      } : undefined}
    >
      {!compactHomePreview && (
        <div className="event-card-image-wrap">
          {currentImage ? (
            <>
              {enableImageModal ? (
                <>
                  {useMobileSafariPdfButton ? (
                    <button
                      type="button"
                      className="event-card-safari-view"
                      onClick={onOpenImageViewer}
                      aria-label={`View event PDF for ${event.title}`}
                    >
                      <span className="event-card-safari-view__label">View Event</span>
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="event-card-image-button"
                        onClick={onOpenImageViewer}
                        aria-label={`Expand image for ${event.title}`}
                      >
                        <EventCardImage
                          file={currentImage}
                          alt={event.title}
                          isPdf={currentFileIsPdf}
                        />
                      </button>
                      <button
                        type="button"
                        className="event-card-expand"
                        onClick={onOpenImageViewer}
                        aria-label={`Expand image for ${event.title}`}
                      >
                        <ExpandIcon />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <EventCardImage
                  file={currentImage}
                  alt={event.title}
                  isPdf={currentFileIsPdf}
                />
              )}
              {images.length > 1 && (
                <>
                  <ImageNavigationButton
                    direction="previous"
                    onClick={() => onImageChange(-1)}
                    label={`Previous image for ${event.title}`}
                  />
                  <ImageNavigationButton
                    direction="next"
                    onClick={() => onImageChange(1)}
                    label={`Next image for ${event.title}`}
                  />
                  <span className="event-card-image-count" aria-live="polite">
                    {currentIndex + 1} / {images.length}
                  </span>
                </>
              )}
            </>
          ) : (
            <div className="event-card-image-placeholder" aria-label="Event placeholder">
              Event
            </div>
          )}
        </div>
      )}
      {event.origin === 'MSIA' && <span className="event-card-origin">MSIA Event</span>}
      <div className="event-card-header">
        <h3>{event.title}</h3>
        {!!event.sponsors?.length && !hasEventEnded(event) && <EventSponsors event={event} />}
      </div>
      <div className="event-details">
        <span>{formatEventDate(event)}</span>
        {event.time && <span>{event.time}</span>}
        <span>{event.location}</span>
      </div>
      <p>{event.description}</p>
      <JoinButton link={event.link} onClick={(clickEvent) => clickEvent.stopPropagation()} />
    </article>
  );
};

const EventCardImage = ({ file, alt, isPdf: fileIsPdf }: { file: string; alt: string; isPdf: boolean }) =>
  fileIsPdf ? (
    <PdfPreview file={file} className="pdf-preview--card" />
  ) : (
    <img className="event-card-image" src={file} alt={alt} />
  );

const ImageNavigationButton = ({
  direction,
  onClick,
  label,
}: {
  direction: 'previous' | 'next';
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    className={`event-card-image-nav event-card-image-nav--${direction}`}
    onClick={onClick}
    aria-label={label}
  >
    <svg className="event-image-arrow" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={direction === 'previous' ? 'M14.5 5.5L8 12l6.5 6.5' : 'M9.5 5.5L16 12l-6.5 6.5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

const ExpandIcon = () => (
  <svg className="event-card-expand-icon" viewBox="0 0 24 24" role="img" aria-hidden="true">
    <path d="M8 4H20V16H18V7.41L5.71 19.71L4.29 18.29L16.59 6H8V4Z" fill="currentColor" />
  </svg>
);

export default EventCard;
